import { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';
import { defaultTargetScore } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.constants';
import {
  hydrateBoosterDraft,
  selectBoosterIsPristine,
  selectBoosterResumeSettled,
  setBoosterReportContent,
  setBoosterReportFacts,
  setBoosterResumeSettled,
  setBoosterStep,
  setBoosterSubmissionId,
  updateTargets,
} from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.slice';
import { MostRecentSubmission } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

type ResumeArgs = Readonly<{
  isRecallLoading: boolean;
  recallFailed: boolean;
  mostRecent: MostRecentSubmission | undefined;
}>;

/**
 * One-time resume decision for the wizard: an in-progress (partial) run is rebuilt onto the
 * Targets step so the student can finish it; a completed run or none leaves the wizard fresh
 * (a completed plan is viewed on the result route, and a returning student starts a new run).
 * The left chat is recalled by the shell from the session id in the URL. Latched via `resumeSettled`.
 */
export const useBoosterResume = ({
  isRecallLoading,
  recallFailed,
  mostRecent,
}: ResumeArgs): { settled: boolean } => {
  const dispatch = useAppDispatch();
  const settled = useAppSelector(selectBoosterResumeSettled);
  const isPristine = useAppSelector(selectBoosterIsPristine);

  useEffect(() => {
    if (settled) return;
    // Never decide over a run already in flight; only a pristine slice may be resumed onto.
    if (!isPristine) return;
    // Wait for the most-recent query to resolve before deciding resume-vs-fresh.
    if (isRecallLoading) return;
    // A genuine fetch failure is not "no submission" — don't latch a fresh run over it.
    if (recallFailed) return;

    const reportContent = mostRecent?.reportContent ?? null;
    const isComplete = mostRecent?.completedAt != null;

    // Only an in-progress run with a captured report resumes into the wizard. A completed run
    // (viewed on the result route) or an empty/report-less one starts the wizard fresh.
    if (!mostRecent || isComplete || !reportContent) {
      dispatch(setBoosterResumeSettled(true));

      return;
    }

    const { reportFacts } = mostRecent;

    // Rebuild the draft and rehydrate the run so completion finalizes this row, not a new one.
    dispatch(hydrateBoosterDraft(mostRecent.restoredDraft));
    dispatch(setBoosterReportFacts(reportFacts));
    dispatch(setBoosterSubmissionId(mostRecent.id));
    // Older/partial runs may lack a saved target score; fall back to the recommended goal.
    if (mostRecent.restoredDraft.targets.targetScore === null) {
      dispatch(
        updateTargets({
          targetTest: reportFacts.test,
          targetScore: defaultTargetScore(
            reportFacts.test,
            reportFacts.currentScore,
            reportFacts.recommendedGoal,
          ),
        }),
      );
    }

    // Keep the frozen report in the slice so completion persists it and the seam doesn't wait on
    // a report that already streamed; the chat bubble comes from the session recall.
    dispatch(setBoosterReportContent(reportContent));
    dispatch(setBoosterStep('TARGETS'));
    dispatch(setBoosterResumeSettled(true));
  }, [settled, isPristine, isRecallLoading, recallFailed, mostRecent, dispatch]);

  return { settled };
};
